import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';
import { Tag, Table, Typography, Flex, Space, Image, Button } from 'antd';
import { PLAN_TYPE_VALUES } from 'constants/users';
import {
  AppointmentStatusType,
  APPOINTMENT_STATUS_VALUES
} from 'constants/appointments';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { PlusSquareOutlined } from '@ant-design/icons';
import { useUIDispatchContext } from 'providers/UI';
import { fetchAppointments } from 'services/api/appointments';
import {
  AppointmentListRow,
  AppointmentTranslatorRef
} from 'types/api/appointments';

import useOrgEffect from 'hooks/useOrgEffect';
import { getFileUrl } from 'utils/file';
import { getStatusColor } from 'utils/appointments';

const PAGE_LIMIT = 5;
const { Text } = Typography;

const AppointmentRequest: FC = () => {
  // translations
  const { t } = useTranslation();
  // navigation
  const navigate = useNavigate();
  const { toggleModal } = useUIDispatchContext();

  const [appointments, setAppointments] = useState<AppointmentListRow[]>([]);
  const [loading, setLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchAppointments({
          page: 1,
          limit: PAGE_LIMIT
        });

        if (!cancelled) setAppointments(res.results);
      } catch {
        // swallow — empty table on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const columns: ColumnProps<AppointmentListRow>[] = [
    {
      title: 'Doctor Name',
      dataIndex: 'doctor',
      key: 'doctor',
      render: value => (
        <Flex gap={8}>
          <Image
            src={getFileUrl(value?.avatar || '')}
            width={32}
            height={32}
            preview={false}
            style={{
              objectFit: 'cover',
              borderRadius: '50%',
              minHeight: 32,
              minWidth: 32
            }}
          />
          <Space>
            <Text>
              {value?.first_name ?? ''} {value?.last_name ?? ''}
            </Text>
          </Space>
        </Flex>
      )
    },
    {
      title: 'Patient Name',
      dataIndex: 'patient',
      key: 'patient',
      render: value => (
        <Flex gap={8}>
          <Image
            src={getFileUrl(value?.avatar || '')}
            width={32}
            height={32}
            preview={false}
            style={{
              objectFit: 'cover',
              borderRadius: '50%',
              minHeight: 32,
              minWidth: 32
            }}
          />
          <Space>
            <Text>
              {value?.first_name ?? ''} {value?.last_name ?? ''}
            </Text>
          </Space>
        </Flex>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: AppointmentStatusType) => (
        <Tag color={getStatusColor(value)}>
          {t(`appointments.${APPOINTMENT_STATUS_VALUES[value]}`)}
        </Tag>
      )
    },
    {
      title: 'Type',
      dataIndex: 'plan',
      key: 'plan',
      render: (_, record) =>
        record.plan?.type === PLAN_TYPE_VALUES.report ? 'Report' : 'Video call'
    },
    {
      title: 'Created at',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value: string) => dayjs(value).format(FULL_DATE_FORMAT)
    },
    {
      title: 'Translator',
      dataIndex: 'appointment_translators',
      key: 'appointment_translators',
      render(value: AppointmentTranslatorRef[] | null, record) {
        if (value?.[0]?.translator) {
          const { first_name, last_name } = value[0].translator;

          return `${first_name || ''} ${last_name || ''}`;
        }

        if (record.translator) {
          return (
            <Button
              type="primary"
              icon={<PlusSquareOutlined />}
              onClick={() =>
                toggleModal('addTranslator', true, {
                  id: String(record.appointment_translators?.[0]?.id ?? '')
                })
              }
            >
              Add translator
            </Button>
          );
        }

        return '-';
      }
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'id',
      align: 'right',
      render: id => (
        <Text
          strong
          style={{ cursor: 'pointer', color: '#2552F3' }}
          onClick={() => navigate(`/appointments/${id}`)}
        >
          View Request
        </Text>
      )
    }
  ];

  return (
    <Table
      dataSource={appointments}
      columns={columns}
      loading={loading}
      rowKey="id"
      className="table_head"
      pagination={false}
    />
  );
};

export default AppointmentRequest;
